import React, { useState, useEffect } from "react";
import "./AssignTask.css";
import CustomAlert from "../../custom_alert/CustomAlert";
import TransLoader from "../../loader/TransLoader";

export default function AssignTask() {
  const [employees, setEmployees] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [loading, setLoading] = useState(false);
  const [alertMessage, setAlertMessage] = useState("");
  const [config, setConfig] = useState(null);

  const [jobTitle, setJobTitle] = useState("");
  const [jobDescription, setJobDescription] = useState("");
  const [startDate, setStartDate] = useState("");
  const [targetDate, setTargetDate] = useState("");
  const [taskImages, setTaskImages] = useState([]);

  const [taskSearch, setTaskSearch] = useState("");
  const [tasks, setTasks] = useState([]);
  const filteredTasks = tasks.filter(
    (t) =>
      t.employee_name.toLowerCase().includes(taskSearch.toLowerCase()) ||
      t.emp_code.toLowerCase().includes(taskSearch.toLowerCase()) ||
      t.job_title.toLowerCase().includes(taskSearch.toLowerCase())
  );

  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editTaskData, setEditTaskData] = useState({
    task_id: "",
    job_title: "",
    job_description: "",
    target_date: "",
    images: [],
  });

  // Fetch config.json
  useEffect(() => {
    fetch("/config.json")
      .then((res) => res.json())
      .then((data) => setConfig(data))
      .catch((err) => console.error("Config fetch error:", err));
  }, []);

  // Fetch employees
  useEffect(() => {
    const fetchEmployees = async () => {
      if (!config) return;
      try {
        setLoading(true);
        const response = await fetch(
          `${config.BASE_URL}${config.GET_ACTIVE_EMPLOYEES}`,
          {
            method: "POST",
            credentials: "include",
            headers: { "X-Requested-With": "RC_EMP_NEW" },
            body: JSON.stringify({ active: 1 }),
          }
        );
        const data = await response.json();
        if (data.status === "success") {
          const mappedEmployees = (data.employees || []).map((emp) => ({
            ...emp,
          }));
          setEmployees(mappedEmployees);
        } else {
          setEmployees([]);
          setAlertMessage(data.message || "No employees found.");
        }
      } catch (error) {
        console.error("Fetch employees failed:", error);
        setAlertMessage("Error fetching employees. Please try again.");
      } finally {
        setLoading(false);
      }
    };
    fetchEmployees();
  }, [config]);

  // Filter employees
  const filteredEmployees = employees.filter(
    (emp) =>
      emp.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      emp.emp_code.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleSelect = (employee) => setSelectedEmployee(employee);

  // Allocate Task
  const handleAllocateTask = async () => {
    if (!selectedEmployee) return setAlertMessage("Please select an employee.");
    if (!jobTitle || !startDate || !targetDate)
      return setAlertMessage("Please fill all mandatory fields.");

    try {
      setLoading(true);
      const formData = new FormData();
      formData.append("emp_code", selectedEmployee.emp_code);
      formData.append("job_title", jobTitle);
      formData.append("job_description", jobDescription || "");
      formData.append("start_date", startDate);
      formData.append("target_date", targetDate);

      taskImages.forEach((file) => formData.append("images[]", file));

      const response = await fetch(`${config.BASE_URL}${config.ALLOCATE_TASK}`, {
        method: "POST",
        credentials: "include",
        headers: { "X-Requested-With": "RC_EMP_NEW" },
        body: formData,
      });
      const data = await response.json();
      if (data.status === "success") {
        setAlertMessage(`Task allocated successfully! Task ID: ${data.task_id}`);
        setJobTitle("");
        setJobDescription("");
        setStartDate("");
        setTargetDate("");
        setTaskImages([]);
        setSelectedEmployee(null);
        fetchTasks();
      } else {
        setAlertMessage(data.message || "Failed to allocate task.");
      }
    } catch (error) {
      console.error(error);
      setAlertMessage("Error allocating task. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const fetchTasks = async () => {
    if (!config) return;
    try {
      setLoading(true);
      const res = await fetch(`${config.BASE_URL}${config.GET_TASKS}`, {
        method: "GET",
        credentials: "include",
        headers: { "X-Requested-With": "RC_EMP_NEW" },
      });
      const data = await res.json();
      if (data.status === "success") {
        setTasks(data.tasks || []);
        // console.log(data);
      }
      else setTasks([]);
    } catch (err) {
      console.error("Error fetching tasks:", err);
      setAlertMessage("Error fetching tasks. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { if (config) fetchTasks(); }, [config]);

  const handleEditClick = (task) => {
    setEditTaskData({
      task_id: task.task_id,
      job_title: task.job_title,
      job_description: task.job_description,
      target_date: task.target_date,
      images: [],
    });
    setEditModalOpen(true);
  };

  const handleCompClick = async (task) => {
    if (!config) return;
    try {
      setLoading(true);
      const formData = new FormData();
      formData.append("task_id", task.task_id);
      const res = await fetch(`${config.BASE_URL}${config.COMP_TASK}`, {
        method: "POST",
        credentials: "include",
        headers: { "X-Requested-With": "RC_EMP_NEW" },
        body: formData,
      });
      const data = await res.json();
      if (data.status === "success") {
        setAlertMessage("Task updated successfully!");
      } else {
        setAlertMessage(data.message || "Failed to update task.");
      }

    } catch (err) {
      console.error(err);
      setAlertMessage("Error updating task.");
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateTask = async (taskId) => {
    try {
      const formData = new FormData();
      formData.append("task_id", taskId);
      formData.append("job_title", editTaskData.job_title);
      formData.append("job_description", editTaskData.job_description);
      formData.append("target_date", editTaskData.target_date);
      editTaskData.images.forEach((file) => formData.append("images[]", file));

      const res = await fetch(`${config.BASE_URL}${config.UPDATE_TASK}`, {
        method: "POST",
        credentials: "include",
        headers: { "X-Requested-With": "RC_EMP_NEW" },
        body: formData,
      });

      const data = await res.json();
      if (data.status === "success") {
        setAlertMessage("Task updated successfully!");
        setEditModalOpen(false);
        fetchTasks();
      } else {
        setAlertMessage(data.message || "Failed to update task.");
      }
    } catch (err) {
      console.error(err);
      setAlertMessage("Error updating task.");
    }
  };

   const handleDeleteClick = async (taskId) => {
  if (!config) return;

  const confirmDelete = window.confirm("Are you sure you want to delete this task?");
  if (!confirmDelete) return;

  try {
    setLoading(true);

    const formData = new FormData();
    formData.append("task_id", taskId);

    const res = await fetch(`${config.BASE_URL}${config.DELETE_TASK}`, {
      method: "POST",
      credentials: "include",
      headers: { "X-Requested-With": "RC_EMP_NEW" },
      body: formData,
    });

    const data = await res.json();

    if (data.status === "success") {
      setAlertMessage("Task deleted successfully!");

      // Remove from frontend immediately
      setTasks((prevTasks) =>
        prevTasks.filter((task) => task.task_id !== taskId)
      );

    } else {
      setAlertMessage(data.message || "Failed to delete task.");
    }

  } catch (err) {
    console.error(err);
    setAlertMessage("Error deleting task.");
  } finally {
    setLoading(false);
  }
};


  return (
    <div className="full-screen-container">
      {loading && <TransLoader />}
      {alertMessage && <CustomAlert message={alertMessage} onClose={() => setAlertMessage("")} />}

      {/* Modal Outside All Cards */}
      {editModalOpen && (
        <div className="modal-backdrop">
          <div className="modal-content">
            <h3>Edit Task #{editTaskData.task_id}</h3>
            <label>Job Title *</label>
            <input
              type="text"
              value={editTaskData.job_title}
              onChange={(e) => setEditTaskData({ ...editTaskData, job_title: e.target.value })}
            />
            <label>Job Description</label>
            <textarea
              value={editTaskData.job_description}
              onChange={(e) => setEditTaskData({ ...editTaskData, job_description: e.target.value })}
            />
            <label>Target Date *</label>
            <input
              type="date"
              value={editTaskData.target_date}
              onChange={(e) => setEditTaskData({ ...editTaskData, target_date: e.target.value })}
            />
            <label>Upload Images</label>
            <input
              type="file"
              multiple
              accept="image/*"
              onChange={(e) => setEditTaskData({ ...editTaskData, images: Array.from(e.target.files) })}
            />
            <button onClick={() => handleUpdateTask(editTaskData.task_id)}>Save</button>
            <button onClick={() => setEditModalOpen(false)}>Cancel</button>
          </div>
        </div>
      )}

      <div className="content-card">
        <h2>Robocoupler Employees List</h2>
        <div className="dropdown-container">
          <input
            type="text"
            placeholder="Search by Name or Employee ID..."
            className="dropdown-search"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          <ul className="dropdown-list">
            {filteredEmployees.length > 0 ? (
              filteredEmployees.map((emp, idx) => (
                <li key={idx} className="dropdown-item" onClick={() => handleSelect(emp)}>
                  <div className="emp-row">
                    <span className="emp-emp_code">{emp.emp_code}</span>
                    <span className="emp-name">{emp.name}</span>
                    <span className="emp-role">{emp.role}</span>
                    <span className="emp-dept">{emp.department}</span>
                  </div>
                </li>
              ))
            ) : (
              <li className="dropdown-item no-result">No results found</li>
            )}
          </ul>
        </div>

        {selectedEmployee && (
          <div className="task-allocation-card">
            <h3>Allocate Task to {selectedEmployee.name} ({selectedEmployee.emp_code})</h3>
            <div className="form-group">
              <label>Job Title *</label>
              <input type="text" value={jobTitle} onChange={(e) => setJobTitle(e.target.value)} />
            </div>
            <div className="form-group">
              <label>Job Description (Optional)</label>
              <textarea value={jobDescription} onChange={(e) => setJobDescription(e.target.value)} />
            </div>
            <div className="form-group">
              <label>Start Date *</label>
              <input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
            </div>
            <div className="form-group">
              <label>Target Date *</label>
              <input type="date" value={targetDate} onChange={(e) => setTargetDate(e.target.value)} />
            </div>
            <div className="form-group">
              <label>Attach Images (Optional)</label>
              <input
                type="file"
                multiple
                accept="image/*"
                onChange={(e) => {
                  const files = Array.from(e.target.files);
                  if (files.length > 5) {
                    setAlertMessage("Maximum 5 images allowed.");
                    setTaskImages([]);
                  } else setTaskImages(files);
                }}
              />
              {taskImages.length > 0 && (
                <div className="selected-images">
                  {taskImages.map((file, i) => (
                    <span key={i}>{file.name}</span>
                  ))}
                </div>
              )}
            </div>
            <button className="allocate-btn" onClick={handleAllocateTask} disabled={loading}>Allocate Task</button>
          </div>
        )}

        <div className="content-card" style={{ marginTop: "30px" }}>
          <h2>All Allocated Tasks</h2>
          <input
            type="text"
            placeholder="Search tasks by employee, code or title..."
            className="dropdown-search"
            value={taskSearch}
            onChange={(e) => setTaskSearch(e.target.value)}
            style={{ marginBottom: "10px" }}
          />
          <div className="table-container">
            <table className="tasks-table">
              <thead>
                <tr>
                  <th>Task ID</th>
                  <th>Employee</th>
                  <th>Code</th>
                  <th>Title</th>
                  <th>Description</th>
                  <th>Start Date</th>
                  <th>Target Date</th>
                  <th>Status</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {filteredTasks.length > 0 ? (
                  filteredTasks.map((task) => (
                    <tr key={task.task_id}>
                      <td>{task.task_id}</td>
                      <td>{task.employee_name}</td>
                      <td>{task.emp_code}</td>
                      <td>{task.job_title}</td>
                       <td style={{ whiteSpace: "pre-wrap" }}>
                        {task.job_description}
                      </td>
                      <td>{task.start_date}</td>
                      <td>{task.target_date}</td>
                      <td style={{ fontWeight: "bold", color: task.task_status === 1 ? "green" : "red" }}>
                        {task.task_status === 1 ? "Completed" : "Pending"}
                      </td>
                      <td style={{ display: "flex", gap: "10px" }}>
                      {Number(task.task_status) !== 1 ? (
                        <>
                          <button onClick={() => handleEditClick(task)}>
                            Edit
                          </button>

                          <button
                            onClick={() => handleCompClick(task)}
                            style={{
                              backgroundColor: "#28a745",
                              color: "white",
                              border: "none",
                              padding: "6px 12px",
                              borderRadius: "4px",
                              cursor: "pointer",
                            }}
                          >
                            Complete
                          </button>

                          <button
                            onClick={() => handleDeleteClick(task.task_id)}
                            style={{
                              backgroundColor: "#dc3545",
                              color: "white",
                              border: "none",
                              padding: "6px 12px",
                              borderRadius: "4px",
                              cursor: "pointer",
                            }}
                          >
                            Delete
                          </button>
                        </>
                      ) : (
                        <span style={{ color: "gray", fontStyle: "italic" }}>
                          No actions
                        </span>
                      )}
                    </td>

                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={9} style={{ textAlign: "center" }}>No tasks found</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}
